/*
  The agent panel outside the session (CP-27). The session, the SME inbox and card, getting started and the Admin page each
  have an agent panel of their own. Everywhere else the shell offers one, and this file decides whom it talks to, by who the
  person is and where they are (BUILD-PLAN 2.7.1):

  - a member of a discipline: Explain mode, the conversation `explain:<membership id>`. It explains, finds things and points to
    them; it changes nothing in the ontology. (An engineer in a session stays in Guide mode: that panel is the session's own.)
  - a member in the practice space: the tutor, `tutor:<membership id>`, the same conversation as getting started.
  - the admin, who has no discipline: the admin helper, `admin:<person id>`.
*/

import type { MeView, MessageContext, Mode } from "../api/types";
import { conversationKeys } from "../agent/conversation";
import { agentContextFor, locate } from "./screenRoutes";

export interface ShellAgentTarget {
  key: string;
  mode: Mode;
  /** The words on the button that opens the panel. */
  launcher: string;
  /** The heading of the panel. */
  title: string;
  /** What the empty panel says. */
  emptyHint: string;
  /** Sent with each message, so "this" means the screen the person is on. */
  context: MessageContext;
}

/** The screens that draw an agent panel of their own: the shell does not add a second one beside it. */
const SCREENS_WITH_THEIR_OWN_PANEL: ReadonlySet<string> = new Set(["live_map", "sme_inbox", "onboarding", "admin_accounts"]);

/**
 * Whom the shell's panel talks to on this screen, or null when it is not offered here. The query string adds the discipline
 * picked in the bar and the element a view of the new design has open to what each message says (rebuild 4.0).
 */
export function shellAgentTarget(me: MeView, pathname: string, search = ""): ShellAgentTarget | null {
  if (SCREENS_WITH_THEIR_OWN_PANEL.has(locate(pathname).screen)) return null;
  const context = agentContextFor(pathname, search);
  const active = me.active;

  if (active) {
    const membership = me.memberships.find((m) => m.id === active.membership_id);
    // A closed discipline can be read and not changed, and a message is a change: no panel, rather than one that refuses.
    if (active.space === "workspace" && membership?.workspace_status === "closed") return null;
    if (active.space === "practice") {
      return {
        key: conversationKeys.tutor(active.membership_id),
        mode: "tutor",
        launcher: "Ask your guide",
        title: "Your guide",
        emptyHint: "Ask about the module you are practising, or what a word on this screen means.",
        context,
      };
    }
    return {
      key: conversationKeys.explain(active.membership_id),
      mode: "explain",
      launcher: "Ask the agent",
      title: "Ask the agent",
      emptyHint: "Ask what a word means, where something is, or what this screen is for. The agent explains and shows you the way; it changes nothing here.",
      context,
    };
  }

  if (me.is_admin) {
    return {
      key: conversationKeys.admin(me.person.id),
      mode: "admin",
      launcher: "Ask the admin helper",
      title: "Admin helper",
      emptyHint: "Ask how to add an account, set the model connection or close a discipline.",
      context,
    };
  }
  return null;
}
